import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import BusinessPartnerModal from "../../components/BusinessPartnerModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filtersRow: {
    marginBottom: theme.spacing(2),
  }
}));

const normalize = value => (value || "").toString().toLowerCase();

const BusinessPartners = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [records, setRecords] = useState([]);
  const [segments, setSegments] = useState([]);
  const [economicGroups, setEconomicGroups] = useState([]);
  const [segmentFilter, setSegmentFilter] = useState("");
  const [economicGroupFilter, setEconomicGroupFilter] = useState("");

  const loadFilters = async () => {
    try {
      const [segmentsRes, economicGroupsRes] = await Promise.all([
        api.get("/segments"),
        api.get("/economic-groups")
      ]);

      setSegments((Array.isArray(segmentsRes?.data?.segments) ? segmentsRes.data.segments : []).filter(item => item && typeof item === "object" && item.id != null));
      setEconomicGroups((Array.isArray(economicGroupsRes?.data?.economicGroups) ? economicGroupsRes.data.economicGroups : []).filter(item => item && typeof item === "object" && item.id != null));
    } catch (err) {
      toastError(err);
    }
  };

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/business-partners", {
        params: { pageNumber: 1 }
      });

      setRecords((Array.isArray(data?.businessPartners) ? data.businessPartners : []).filter(item => item && typeof item === "object" && item.id != null));
      setLoading(false);
    } catch (err) {
      setLoading(false);
      toastError(err);
      setRecords([]);
    }
  };

  useEffect(() => {
    loadFilters();
    fetchRecords();
  }, []);

  const visibleRecords = useMemo(() => {
    const q = normalize(searchParam);

    return (Array.isArray(records) ? records : []).filter((record) => {
      const matchesSearch =
        !q ||
        normalize(record?.name).includes(q) ||
        normalize(record?.document).includes(q) ||
        normalize(record?.email).includes(q) ||
        normalize(record?.phone).includes(q) ||
        normalize(record?.addressCity).includes(q) ||
        normalize(record?.segment?.name).includes(q) ||
        normalize(record?.subsegment?.name).includes(q) ||
        normalize(record?.economicGroup?.name).includes(q);

      const matchesSegment = segmentFilter
        ? Number(record?.segmentId) === Number(segmentFilter)
        : true;

      const matchesEconomicGroup = economicGroupFilter
        ? Number(record?.economicGroupId) === Number(economicGroupFilter)
        : true;

      return matchesSearch && matchesSegment && matchesEconomicGroup;
    });
  }, [records, searchParam, segmentFilter, economicGroupFilter]);

  const handleOpenModal = () => {
    setSelectedRecord(null);
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/business-partners/${id}`);
      toast.success("Parceiro de Negócios removido com sucesso");
      setRecords(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      toastError(err);
    }
    setConfirmOpen(false);
    setDeletingRecord(null);
  };

  return (
    <MainContainer>
      {modalOpen && (
        <BusinessPartnerModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          businessPartnerId={selectedRecord?.id}
          onSave={fetchRecords}
        />
      )}

      <ConfirmationModal
        title={
          deletingRecord
            ? `Excluir ${deletingRecord.name}?`
            : "Confirmar exclusão"
        }
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeletingRecord(null);
        }}
        onConfirm={() => handleDelete(deletingRecord.id)}
      >
        Esta ação não pode ser revertida.
      </ConfirmationModal>

      <MainHeader>
        <Title>Empresas</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder="Pesquisar"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenModal}
          >
            Nova Empresa
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Grid container spacing={2} className={classes.filtersRow}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Filtrar por Segmento</InputLabel>
            <Select
              value={segmentFilter}
              onChange={(e) => setSegmentFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              {segments.filter(item => item && typeof item === "object" && item.id != null).map(item => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Filtrar por Grupo Econômico</InputLabel>
            <Select
              value={economicGroupFilter}
              onChange={(e) => setEconomicGroupFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              {economicGroups.filter(item => item && typeof item === "object" && item.id != null).map(item => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Documento</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>Segmento</TableCell>
              <TableCell>Subsegmento</TableCell>
              <TableCell>Grupo Econômico</TableCell>
              <TableCell>Cidade</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRecords.filter(item => item && typeof item === "object" && item.id != null).map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record?.name || ""}</TableCell>
                <TableCell>{record?.document || ""}</TableCell>
                <TableCell>{record?.email || ""}</TableCell>
                <TableCell>{record?.phone || ""}</TableCell>
                <TableCell>{record?.segment?.name || ""}</TableCell>
                <TableCell>{record?.subsegment?.name || ""}</TableCell>
                <TableCell>{record?.economicGroup?.name || ""}</TableCell>
                <TableCell>{record?.addressCity || ""}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleEdit(record)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setDeletingRecord(record);
                      setConfirmOpen(true);
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {loading &&
              Array.from({ length: 5 }).map((_, i) => (
                <TableRowSkeleton key={i} columns={9} />
              ))}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default BusinessPartners;
